import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deleteFaqKey, updateFaqKeyName } from '../../../api/faq'
import { ReactComponent as PlusCircle } from '../../../assets/icons/add-circle.svg'
import { ReactComponent as CheckmarkCircle } from '../../../assets/icons/checkmark-circle.svg'
import { ReactComponent as Close } from '../../../assets/icons/close.svg'
import { ReactComponent as Pencil } from '../../../assets/icons/pencil.svg'
import { ReactComponent as Search } from '../../../assets/icons/search.svg'
import { ReactComponent as Thrash } from '../../../assets/icons/trash.svg'

import styles from './faq-item.module.scss'

interface IFaqItemProps {
    data: string[]
    onItemClick: (item: string) => void
    onAddItemClick: (newItem: string) => void
    fetchFaqData: () => void
    clearCurrentFaqDataState: () => void
}

function FaqItems(props: IFaqItemProps) {
    const {
        data,
        onItemClick,
        onAddItemClick,
        fetchFaqData,
        clearCurrentFaqDataState,
    } = props
    const { t } = useTranslation()

    const [searchTerm, setSearchTerm] = useState('')
    const [activeIndex, setActiveIndex] = useState<number | null>(null)
    const [editIndex, setEditIndex] = useState<number | null>(null)
    const [editValue, setEditValue] = useState<string>('')
    const [newItem, setNewItem] = useState<string>('')
    const [isAdding, setIsAdding] = useState(false)

    const filteredItems = data.filter((data) =>
        data.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const handleItemClick = (index: number) => {
        if (editIndex === null) {
            setActiveIndex(index)
            onItemClick(filteredItems[index])
        }
    }

    const handleEditClick = (index: number) => {
        setEditIndex(index)
        setEditValue(filteredItems[index])
    }

    const handleSaveClick = async (index: number) => {
        const confirmUpdate = confirm(t('save_key_confirm'))
        if (!confirmUpdate) return

        if (editValue !== filteredItems[index]) {
            await updateFaqKeyName(filteredItems[index], editValue)
            fetchFaqData()
        }
        setEditIndex(null)
    }

    const handleAddItemClick = () => {
        if (newItem.trim() !== '') {
            onAddItemClick(newItem)
            onItemClick(newItem)
            setNewItem('')
            setSearchTerm('')
            setActiveIndex(0)
            setIsAdding(false)
        }
    }

    const handleDeleteClick = async (index: number) => {
        const confirmDelete = confirm(t('delete_key_confirm'))
        if (!confirmDelete) return

        const keyToDelete = filteredItems[index]
        await deleteFaqKey(keyToDelete)
        fetchFaqData()
    }

    const handleAddNewClick = () => {
        setIsAdding(true)
        setActiveIndex(null)
        setEditIndex(null)
    }

    const handleCancelAdd = () => {
        setIsAdding(false)
        setNewItem('')
    }

    const handleCancelEdit = () => {
        setEditIndex(null)
        setEditValue('')
    }

    const handleSearchTermChange = (searchTerm: string) => {
        setSearchTerm(searchTerm)
        setActiveIndex(null)
        clearCurrentFaqDataState()
    }

    const setAddButtonActive = () => !newItem.trim()

    const setEditButtonActive = () => !editValue.trim()

    return (
        <div className={styles.faqItem}>
            <div className={styles.faqItem__header}>
                <h2>{t('key')}</h2>
                <div>
                    {!isAdding && (
                        <button
                            className={styles.faqItem__btn}
                            onClick={() => handleAddNewClick()}
                        >
                            <PlusCircle />
                            {t('add_new')}
                        </button>
                    )}
                </div>
            </div>

            {isAdding && (
                <div className={styles.faqItem__addingForm}>
                    <input
                        className={styles.faqItem__addingForm__input}
                        type="text"
                        value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                    />
                    <button
                        onClick={handleAddItemClick}
                        className={`${styles.faqItem__borderBtn} ${
                            setAddButtonActive()
                                ? styles.faqItem__disabledBtn
                                : ''
                        }`}
                    >
                        <CheckmarkCircle />
                    </button>
                    <button
                        className={styles.faqItem__borderBtn}
                        onClick={handleCancelAdd}
                    >
                        <Close />
                    </button>
                </div>
            )}

            <div className={styles.faqItem__body}>
                <label className={styles.faqItem__label} htmlFor="search">
                    <input
                        className={styles.faqItem__input}
                        name="search"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => handleSearchTermChange(e.target.value)}
                    />
                    <Search className={styles.faqItem__icon} alt="Search" />
                </label>

                <p className={styles.faqItem__resultsCount}>
                    {filteredItems.length} results
                </p>

                {filteredItems.map((faqItem, index) => (
                    <div
                        key={index}
                        className={index === activeIndex ? styles.active : ''}
                    >
                        {editIndex === index ? (
                            <>
                                <div className={styles.faqItem__row}>
                                    <input
                                        className={styles.faqItem__row__input}
                                        type="text"
                                        value={editValue}
                                        onChange={(e) =>
                                            setEditValue(e.target.value)
                                        }
                                    />

                                    <div
                                        className={styles.faqItem__row__buttons}
                                    >
                                        <button
                                            onClick={() =>
                                                handleSaveClick(index)
                                            }
                                            className={`${
                                                styles.faqItem__borderBtn
                                            } ${
                                                setEditButtonActive()
                                                    ? styles.faqItem__disabledBtn
                                                    : ''
                                            }`}
                                        >
                                            <CheckmarkCircle />
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            className={
                                                styles.faqItem__borderBtn
                                            }
                                        >
                                            <Close />
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className={styles.faqItem__row}>
                                <div
                                    className={styles.faqItem__row__text}
                                    onClick={() => handleItemClick(index)}
                                >
                                    {faqItem}
                                </div>

                                <div className={styles.faqItem__row__buttons}>
                                    <button
                                        className={styles.faqItem__borderBtn}
                                        onClick={() => handleEditClick(index)}
                                    >
                                        <Pencil />
                                    </button>
                                    <button
                                        className={styles.faqItem__borderBtn}
                                        onClick={() => handleDeleteClick(index)}
                                    >
                                        <Thrash />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default FaqItems
